/**
 * Utility functions for Laporan Rekap Master.
 */

export const MonthOptions = [
  { label: "Januari", value: 1 },
  { label: "Februari", value: 2 },
  { label: "Maret", value: 3 },
  { label: "April", value: 4 },
  { label: "Mei", value: 5 },
  { label: "Juni", value: 6 },
  { label: "Juli", value: 7 },
  { label: "Agustus", value: 8 },
  { label: "September", value: 9 },
  { label: "Oktober", value: 10 },
  { label: "November", value: 11 },
  { label: "Desember", value: 12 },
];

/**
 * Build a Map assigning sequential codes (TP-001, TP-002, ...) to unique tp_id
 * based on order of appearance in the rows.
 *
 * @param {Array<Object>} rows
 * @returns {Map<any, string>}
 */
export const buildTpCodeMap = (rows = []) => {
  const map = new Map();
  if (!Array.isArray(rows)) return map;
  let count = 0;
  rows.forEach((row) => {
    const tpId = row?.tp_id;
    if (tpId != null && !map.has(tpId)) {
      count += 1;
      map.set(tpId, `TP-${String(count).padStart(3, "0")}`);
    }
  });
  return map;
};

/**
 * Calculate density ratio (total_larva / total_catch * 100) with 2 decimal places.
 * Returns "-" if total_catch <= 0 or values are not valid numbers.
 *
 * @param {Object} row
 * @returns {string}
 */
export const getDensity = (row = {}) => {
  const catchVal = parseFloat(row?.total_catch);
  const larvaVal = parseFloat(row?.total_larva);

  if (!isNaN(catchVal) && !isNaN(larvaVal) && catchVal > 0) {
    return ((larvaVal / catchVal) * 100).toFixed(2);
  }
  return "-";
};

/**
 * Format larva phase list into comma-separated string or fallback to "-".
 *
 * @param {any} phase
 * @returns {string}
 */
export const formatLarvaPhase = (phase) => {
  if (Array.isArray(phase)) {
    return phase.length > 0 ? phase.join(", ") : "-";
  }
  return phase || "-";
};

/**
 * Check if a specific intervention type exists in intervention_done array.
 *
 * @param {any} interventions
 * @param {string} type
 * @returns {"Ya" | "Tidak"}
 */
export const hasInterventionType = (interventions, type) => {
  if (!Array.isArray(interventions)) return "Tidak";
  return interventions.includes(type) ? "Ya" : "Tidak";
};

/**
 * Extract other intervention text starting with "Lainnya_".
 *
 * @param {any} interventions
 * @returns {string}
 */
export const getInterventionOthers = (interventions) => {
  if (!Array.isArray(interventions)) return "-";
  const found = interventions.find(
    (x) => typeof x === "string" && x.startsWith("Lainnya_")
  );
  return found ? found.replace("Lainnya_", "") : "-";
};

/**
 * Check whether "Belum Intervensi" applies.
 *
 * @param {any} interventions
 * @returns {"Ya" | "Tidak"}
 */
export const isBelumIntervensi = (interventions) => {
  if (!Array.isArray(interventions)) return "Tidak";
  return interventions.some(
    (x) =>
      typeof x === "string" &&
      (x.startsWith("Belum") || x === "Belum dilakukan Intervensi")
  )
    ? "Ya"
    : "Tidak";
};

/**
 * Extract planned intervention text from items starting with "Belum_".
 *
 * @param {any} interventions
 * @returns {string}
 */
export const getInterventionBelum = (interventions) => {
  if (!Array.isArray(interventions)) return "-";
  const found = interventions.find(
    (x) =>
      typeof x === "string" &&
      (x.startsWith("Belum_") || x === "Belum dilakukan Intervensi")
  );
  if (!found) return "-";
  return found.startsWith("Belum_")
    ? found.replace("Belum_", "")
    : "Belum dilakukan Intervensi";
};
